import { ComponentProps, useEffect } from "react";

import { BsMoonFill, BsMoonStarsFill, BsSunFill, BsSunriseFill } from "react-icons/bs";
import { Flex, FormControl, Grid, HStack, Icon, Stack, Text } from "@chakra-ui/react";
import { MySelect } from "./ui";
import { useSlide } from "../public/slide";
import { TimeState, useQuizActions, useQuizSnapshot } from "./state";
import { SlidePropsTime } from "../public/types";
import { useTranslation } from "gatsby-plugin-react-i18next";

import { IconType } from "react-icons";
import { wait } from "@base/utils/wait";

const hours = Array.from({ length: 12 }, (_, i) => i + 1);
const minutes = Array.from({ length: 60 }, (_, i) => i);
const meridiem = ["am", "pm"] as const;

const DEFAULT_TIME = { hour: 12, minute: 0, meridiem: "am" as const };

export type TimePickerProps = {} & SlidePropsTime;

export function TimePicker(props: TimePickerProps) {
  const slide = useSlide();
  const snap = useQuizSnapshot();
  const actions = useQuizActions();
  const { t } = useTranslation();

  const state = snap.slideStateByID[slide.id] as TimeState;

  useEffect(() => {
    if (!state.value) {
      actions.setTimeValue(slide.id, {
        ...DEFAULT_TIME,
      });
    }
  }, [state.value]);

  const handleExactTimePick = (value: string, part: "hour" | "minute" | "meridiem") => {
    const v = part === "meridiem" ? value : parseInt(value);
    actions.setTimeValue(slide.id, {
      ...DEFAULT_TIME,
      ...state.value,
      quickPeriod: undefined,
      [part]: v,
    });
  };

  return (
    <Stack>
      <HStack>
        <FormControl>
          <MySelect
            id="hour"
            value={state.value?.hour ?? 0}
            onChange={(e) => {
              handleExactTimePick(e.target.value, "hour");
            }}
          >
            {hours.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </MySelect>
        </FormControl>
        <FormControl>
          <MySelect
            id="minute"
            value={state.value?.minute ?? 0}
            onChange={(e) => {
              handleExactTimePick(e.target.value, "minute");
            }}
          >
            {minutes.map((m) => (
              <option key={m} value={m}>
                {m.toString().padStart(2, "0")}
              </option>
            ))}
          </MySelect>
        </FormControl>
        <FormControl>
          <MySelect
            id="meridiem"
            value={state.value?.meridiem ?? "am"}
            onChange={(e) => {
              const v = e.target.value as "am" | "pm";
              handleExactTimePick(v, "meridiem");
            }}
          >
            {meridiem.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </MySelect>
        </FormControl>
      </HStack>

      <Text textAlign={"center"} fontSize={"xs"} mt={6}>
        {t("quizLib.timepicker.orExactHour", {
          defaultValue: "Or, if you don't know the exact hour",
        })}
      </Text>

      <Grid
        mt={2}
        autoFlow={"row"}
        gridTemplateColumns={"repeat(4, 1fr)"}
        justifyItems={"center"}
        autoColumns={"min-content"}
        gridGap={2}
      >
        <CustomButton
          isSelected={state.value?.quickPeriod === "Morning"}
          onClick={async () => {
            actions.setTimeValue(slide.id, {
              hour: 6,
              minute: 0,
              meridiem: "am",
              quickPeriod: "Morning",
            });
            await wait(150);
            const ok = actions.submitQuestion();
            if (ok) {
              actions.goToNext();
            }
          }}
          flex={1}
          flexShrink={0}
          title={t("quizLib.timepicker.morning", { defaultValue: "Morning" })}
          subtitle={t("quizLib.timepicker.morningTime", { defaultValue: "6am - 12pm" })}
          icon={BsSunriseFill}
        />
        <CustomButton
          isSelected={state.value?.quickPeriod === "Noon"}
          onClick={async () => {
            actions.setTimeValue(slide.id, {
              hour: 12,
              minute: 0,
              meridiem: "pm",
              quickPeriod: "Noon",
            });
            await wait(150);
            const ok = actions.submitQuestion();
            if (ok) {
              actions.goToNext();
            }
          }}
          flex={1}
          flexShrink={0}
          title={t("quizLib.timepicker.noon", { defaultValue: "Noon" })}
          subtitle={t("quizLib.timepicker.noonTime", { defaultValue: "12pm - 6pm" })}
          icon={BsSunFill}
        />
        <CustomButton
          isSelected={state.value?.quickPeriod === "Evening"}
          onClick={async () => {
            actions.setTimeValue(slide.id, {
              hour: 6,
              minute: 0,
              meridiem: "pm",
              quickPeriod: "Evening",
            });
            await wait(150);
            const ok = actions.submitQuestion();
            if (ok) {
              actions.goToNext();
            }
          }}
          flex={1}
          flexShrink={0}
          title={t("quizLib.timepicker.evening", { defaultValue: "Evening" })}
          subtitle={t("quizLib.timepicker.eveningTime", { defaultValue: "6pm - 12am" })}
          icon={BsMoonFill}
        />
        <CustomButton
          isSelected={state.value?.quickPeriod === "Night"}
          onClick={async () => {
            actions.setTimeValue(slide.id, {
              hour: 12,
              minute: 0,
              meridiem: "am",
              quickPeriod: "Night",
            });
            await wait(150);
            const ok = actions.submitQuestion();
            if (ok) {
              actions.goToNext();
            }
          }}
          flex={1}
          flexShrink={0}
          title={t("quizLib.timepicker.night", { defaultValue: "Night" })}
          subtitle={t("quizLib.timepicker.nightTime", { defaultValue: "12am - 6am" })}
          icon={BsMoonStarsFill}
        />
      </Grid>
    </Stack>
  );
}

function CustomButton({
  icon,
  title,
  subtitle,
  isSelected,
  ...rest
}: ComponentProps<typeof Stack> & {
  title: string;
  subtitle: string;
  icon: IconType;
  isSelected: boolean;
}) {
  return (
    <Stack
      as="button"
      alignItems={"center"}
      backgroundColor={isSelected ? "bg.400" : "bg.200"}
      outline={`${isSelected ? 3 : 0}px solid`}
      outlineColor={"primary.700"}
      px={2}
      py={3}
      borderRadius={"lg"}
      width={"full"}
      {...rest}
    >
      <Flex
        direction={"column"}
        alignItems={"center"}
        justifyContent={"center"}
        borderRadius={"full"}
      >
        <Icon as={icon} color="primary.400" />
      </Flex>
      <Stack spacing={0} color="text.800">
        <Text fontSize={"xs"} fontWeight={"semibold"}>
          {title}
        </Text>
        <Text width={"100%"} fontSize={"2xs"} fontWeight={"semibold"}>
          {subtitle}
        </Text>
      </Stack>
    </Stack>
  );
}
